'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import axios from 'axios'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import Turnstile from 'react-turnstile'
import { z } from 'zod'

import { Alert } from '@/components/atoms/alert'
import { Input } from '@/components/atoms/form-input'
import { Textarea } from '@/components/atoms/form-textarea'
import { Loading } from '@/components/atoms/icons'
import { Button } from '@/components/atoms/ui/button'
import { Form } from '@/components/atoms/ui/form'
import { toast } from '@/components/atoms/ui/use-toast'

import { cn } from '@/lib/utils'

import { defaultValues, formSchema } from './schema'

export const ContactForm = ({ className }: { className?: string }) => {
  const [loading, setLoading] = useState(false)
  const [alert, setAlert] = useState<'success' | 'error' | null>(null)
  const [isCaptchaChecked, setIsCaptchaChecked] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!isCaptchaChecked) {
      toast({ title: 'Silakan tandai CAPTCHA untuk melanjutkan.', variant: 'destructive' })

      return
    }

    setLoading(true)

    const bodyFormData = new FormData()
    bodyFormData.append('Subject', 'Tentang Kami - Hubungi Kami Form')
    bodyFormData.append(
      'Text-part',
      `
      Nama: ${values.fullName},
      Perusahaan: ${values.companyName},
      Telepon: ${values.whatsapp},
      Email: ${values.email},
      Pesan: ${values.message}`
    )
    bodyFormData.append(
      'Html-part',
      `
      <h3>Nama: ${values.fullName}</h3>
      <h3>Perusahaan: ${values.companyName}</h3>
      <h3>Telepon: ${values.whatsapp}</h3>
      <h3>Email: ${values.email}</h3>
      <h3>Pesan: ${values.message}</h3>
    `
    )

    try {
      const response = await axios({
        method: 'post',
        url: 'https://easybiz-id.netlify.app/.netlify/functions/send-email',
        data: bodyFormData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      if (response.status === 200) {
        form.reset()
        setAlert('success')
      }
    } catch (error) {
      setAlert('error')
    } finally {
      setLoading(false)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={cn('space-y-6', className)}>
        <div className="space-y-4">
          {alert && (
            <Alert
              type={alert}
              message={
                alert === 'error'
                  ? 'Pesan gagal dikirim. Harap coba beberapa saat lagi.'
                  : 'Berhasil terkirim! Tim kami akan menghubungi Anda.'
              }
            />
          )}
          <Input name="fullName" placeholder="Nama wajib diisi *" {...form} />
          <Input name="companyName" placeholder="Nama Perusahaan" {...form} />
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-3">
            <Input name="whatsapp" placeholder="Nomor WhatsApp *" {...form} />
            <Input name="email" placeholder="Email Anda" {...form} />
          </div>
          <Textarea name="message" placeholder="Pesan atau pertanyaan Anda *" {...form} />
        </div>
        <Turnstile
          onVerify={() => setIsCaptchaChecked(true)}
          className="flex justify-center"
          sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '0x4AAAAAAAdavGli1HsHq-G1'}
        />
        <Button size="lg" disabled={loading} className="w-full text-base font-medium gtm-contact-email" type="submit">
          {loading ? <Loading /> : 'Kirim'}
        </Button>
      </form>
    </Form>
  )
}
